<script setup>
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'

mapboxgl.accessToken =
    'pk.eyJ1IjoiYWFyY2lsYTE4IiwiYSI6ImNsM2V6YWdscjBuZDMzY3A3M2FtMDl6YWgifQ.adh5HQ_qhIy3cjG1a-pSAQ'

const props = defineProps({
    farms: { type: Array, default: () => [] },
})

const mapContainer = ref(null)
const mapReady = ref(false)

let map = null
let popup = null

const emptyCollection = () => ({ type: 'FeatureCollection', features: [] })

const validCoordinate = coordinate => Array.isArray(coordinate)
    && coordinate.length >= 2
    && Number.isFinite(Number(coordinate[0]))
    && Number.isFinite(Number(coordinate[1]))

const centerForFarm = farm => {
    if (Number.isFinite(Number(farm.longitude)) && Number.isFinite(Number(farm.latitude))) {
        return [Number(farm.longitude), Number(farm.latitude)]
    }

    const boundary = (farm.boundary || []).filter(validCoordinate)
    if (!boundary.length) {
        return null
    }

    const totals = boundary.reduce((result, coordinate) => [
        result[0] + Number(coordinate[0]),
        result[1] + Number(coordinate[1]),
    ], [0, 0])

    return [totals[0] / boundary.length, totals[1] / boundary.length]
}

const farmProperties = farm => ({
    id: farm.id,
    name: farm.name || 'Finca sin nombre',
    producer: farm.producer || 'Productor no disponible',
    size: farm.size ? `${farm.size} ${farm.area_unit === 'ha' ? 'ha' : 'm²'}` : 'Sin tamaño registrado',
    harvestCount: Number(farm.harvest_count || 0),
    products: (farm.products || []).join(', ') || 'Sin productos registrados',
})

const polygonFeatures = () => props.farms.flatMap(farm => {
    const boundary = (farm.boundary || [])
        .filter(validCoordinate)
        .map(coordinate => [Number(coordinate[0]), Number(coordinate[1])])

    if (boundary.length < 3) {
        return []
    }

    const first = boundary[0]
    const last = boundary[boundary.length - 1]
    if (first[0] !== last[0] || first[1] !== last[1]) {
        boundary.push([...first])
    }

    return [{
        type: 'Feature',
        properties: farmProperties(farm),
        geometry: { type: 'Polygon', coordinates: [boundary] },
    }]
})

const pointFeatures = () => props.farms.flatMap(farm => {
    const center = centerForFarm(farm)
    if (!center) {
        return []
    }

    return [{
        type: 'Feature',
        properties: farmProperties(farm),
        geometry: { type: 'Point', coordinates: center },
    }]
})

const mapData = () => ({
    polygons: { type: 'FeatureCollection', features: polygonFeatures() },
    points: { type: 'FeatureCollection', features: pointFeatures() },
})

const fitAllFarms = (animate = true) => {
    if (!mapReady.value || !map || !props.farms.length) {
        return
    }

    const coordinates = props.farms.flatMap(farm => {
        const boundary = (farm.boundary || []).filter(validCoordinate)
        if (boundary.length >= 3) {
            return boundary.map(coordinate => [Number(coordinate[0]), Number(coordinate[1])])
        }

        const center = centerForFarm(farm)
        return center ? [center] : []
    })

    if (!coordinates.length) {
        return
    }

    const bounds = coordinates.reduce(
        (result, coordinate) => result.extend(coordinate),
        new mapboxgl.LngLatBounds(coordinates[0], coordinates[0])
    )

    map.fitBounds(bounds, {
        padding: { top: 70, right: 60, bottom: 70, left: 60 },
        maxZoom: 15,
        duration: animate ? 700 : 0,
    })
}

const refreshMap = (fit = true) => {
    if (!mapReady.value || !map) {
        return
    }

    const data = mapData()
    const polygonSource = map.getSource('admin-farm-polygons')
    const pointSource = map.getSource('admin-farm-points')

    if (polygonSource) {
        polygonSource.setData(data.polygons)
    }
    if (pointSource) {
        pointSource.setData(data.points)
    }

    if (fit) {
        fitAllFarms()
    }
}

const popupContent = properties => {
    const container = document.createElement('div')
    container.className = 'admin-map-popup'

    const title = document.createElement('strong')
    title.textContent = properties.name
    container.appendChild(title)

    const rows = [
        properties.producer,
        properties.size,
        `${properties.harvestCount} ${Number(properties.harvestCount) === 1 ? 'cosecha' : 'cosechas'}`,
        properties.products,
    ]

    rows.forEach(value => {
        const row = document.createElement('span')
        row.textContent = value
        container.appendChild(row)
    })

    return container
}

const showPopup = event => {
    const feature = event.features && event.features[0]
    if (!feature) {
        return
    }

    if (popup) {
        popup.remove()
    }

    popup = new mapboxgl.Popup({ closeButton: true, offset: 12 })
        .setLngLat(event.lngLat)
        .setDOMContent(popupContent(feature.properties))
        .addTo(map)
}

const setPointer = () => { map.getCanvas().style.cursor = 'pointer' }
const clearPointer = () => { map.getCanvas().style.cursor = '' }

onMounted(() => {
    map = new mapboxgl.Map({
        container: mapContainer.value,
        style: 'mapbox://styles/mapbox/streets-v11',
        center: [-74.2973, 4.5709],
        zoom: 4.5,
        cooperativeGestures: false,
    })

    map.addControl(new mapboxgl.NavigationControl(), 'top-right')
    map.addControl(new mapboxgl.FullscreenControl(), 'top-right')

    map.on('load', () => {
        const data = mapData()

        map.addSource('admin-farm-polygons', { type: 'geojson', data: data.polygons })
        map.addSource('admin-farm-points', { type: 'geojson', data: data.points })

        map.addLayer({
            id: 'admin-farm-fill',
            type: 'fill',
            source: 'admin-farm-polygons',
            paint: {
                'fill-color': '#F3E022',
                'fill-opacity': 0.48,
            },
        })

        map.addLayer({
            id: 'admin-farm-outline-halo',
            type: 'line',
            source: 'admin-farm-polygons',
            paint: {
                'line-color': '#ffffff',
                'line-opacity': 0.95,
                'line-width': 7,
            },
        })

        map.addLayer({
            id: 'admin-farm-outline',
            type: 'line',
            source: 'admin-farm-polygons',
            paint: {
                'line-color': '#03662E',
                'line-width': 4,
            },
        })

        map.addLayer({
            id: 'admin-farm-centers',
            type: 'circle',
            source: 'admin-farm-points',
            paint: {
                'circle-color': '#03662E',
                'circle-radius': ['interpolate', ['linear'], ['zoom'], 3, 7, 12, 10],
                'circle-stroke-color': '#ffffff',
                'circle-stroke-width': 3,
            },
        })

        map.addLayer({
            id: 'admin-farm-labels',
            type: 'symbol',
            source: 'admin-farm-points',
            minzoom: 5,
            layout: {
                'text-field': ['get', 'name'],
                'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
                'text-size': 13,
                'text-offset': [0, 1.45],
                'text-anchor': 'top',
                'text-allow-overlap': false,
            },
            paint: {
                'text-color': '#063A20',
                'text-halo-color': '#ffffff',
                'text-halo-width': 2,
            },
        })

        ;['admin-farm-fill', 'admin-farm-centers'].forEach(layer => {
            map.on('click', layer, showPopup)
            map.on('mouseenter', layer, setPointer)
            map.on('mouseleave', layer, clearPointer)
        })

        mapReady.value = true
        fitAllFarms(false)
    })
})

watch(() => props.farms, () => refreshMap(), { deep: true })

onBeforeUnmount(() => {
    if (popup) {
        popup.remove()
    }
    if (map) {
        map.remove()
        map = null
    }
})
</script>

<template>
    <section class="relative overflow-hidden bg-white border shadow-sm admin-production-map-shell rounded-3xl border-light_gray">
        <div class="absolute z-10 flex items-center gap-3 px-4 py-3 bg-white shadow-md admin-production-map-legend rounded-2xl">
            <span class="inline-block w-5 h-5 border-2 rounded border-enriqueto_green bg-enriqueto_yellow"></span>
            <div>
                <p class="text-sm font-bold leading-tight text-enriqueto_green">Fincas registradas</p>
                <p class="text-xs text-gray">Haz clic en un lindero para ver los detalles</p>
            </div>
        </div>

        <button
            v-if="farms.length"
            type="button"
            class="absolute z-10 px-4 py-2 text-sm font-bold bg-white border shadow-md admin-production-map-reset rounded-xl border-light_gray text-enriqueto_green"
            @click="fitAllFarms"
        >
            Ver todas
        </button>

        <div ref="mapContainer" class="w-full admin-production-map"></div>

        <div v-if="!farms.length" class="absolute inset-0 z-20 flex items-center justify-center p-8 bg-white bg-opacity-90">
            <p class="font-bold text-center text-enriqueto_green">No hay fincas que coincidan con la búsqueda.</p>
        </div>
    </section>
</template>

<style scoped>
.admin-production-map {
    height: min(68vh, 760px);
    min-height: 560px;
}

.admin-production-map-legend {
    left: 1rem;
    top: 1rem;
}

.admin-production-map-reset {
    bottom: 1.5rem;
    left: 1rem;
}

.admin-production-map-reset:hover {
    background: #03662E;
    color: #ffffff;
}

:deep(.admin-map-popup) {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    min-width: 190px;
    padding: 0.25rem;
}

:deep(.admin-map-popup strong) {
    color: #03662E;
    font-size: 1rem;
    margin-bottom: 0.2rem;
}

:deep(.admin-map-popup span) {
    color: #4b5563;
    font-size: 0.78rem;
}

@media (max-width: 640px) {
    .admin-production-map {
        height: 62vh;
        min-height: 480px;
    }

    .admin-production-map-legend {
        max-width: calc(100% - 5.5rem);
    }
}
</style>
